import { Component, inject, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ApBaseComponent } from 'webcommon/core-ui';
import { ApCustomDateRange, FindControlAdditionalSearchParametersDto, FindControlAdditionalSearchParametersType, FindControlRequiredSearchParametersDto, GenericDrugDetailsDto, Guid, GuidStrValue, IColumnPk, LabResultSortType, LabTemplateDetailsDto, VisibleToEnum } from 'webcommon/shared';
import { FilterConfigEnum, GroupTypeEnum, TestTypeValuesEnum } from './models';
import { FindControlIdService } from 'webcommon/legacy-common';
import { PatientResultsFilterDetailsFormControls } from './models/PatientResultsFilterFormControls';
import { debounceTime, startWith, tap } from 'rxjs/operators';

@Component({
  selector: 'patient-results-filter-form',
  templateUrl: './patient-results-filter-form.component.html',
  styleUrls: ['./patient-results-filter-form.component.less'],
})
export class PatientResultsFilterFormComponent extends ApBaseComponent implements OnInit {
  @Input() baseId: string;
  @Input() patientResultsFilterDetailsFormGroup: FormGroup<PatientResultsFilterDetailsFormControls>;
  @Input() isFormSubmitted = false;
  @Input() customDateRange: ApCustomDateRange = {};
  @Output() customDateRangeChange = new EventEmitter<ApCustomDateRange>();
  public readonly findControlIdService = inject(FindControlIdService);

  public groupTypesList = [
    { label: 'Attachment Type', controlName: LabResultSortType.Attachment },
    { label: 'Date', controlName: LabResultSortType.Date },
    { label: 'Results', controlName: LabResultSortType.Results },
    { label: 'Lab Template', controlName: LabResultSortType.Template },
    { label: 'Flowsheet', controlName: LabResultSortType.Flowsheet }
  ];

  public testTypesList = [
    { label: 'Show Test Items', controlName: TestTypeValuesEnum.TestItems },
    { label: 'Merge Lab Templates by LOINC', controlName: TestTypeValuesEnum.LabTemplatesByLoinc },
    { label: 'Merge Test Items by Mappping/LOINC', controlName: TestTypeValuesEnum.TestItemsByMappingOrLonic },
  ];

  public FilterTypesList = [
    { label: 'Me', controlName: VisibleToEnum.Me },
    { label: 'Everyone', controlName: VisibleToEnum.Everyone },
  ];

  public FilterConfigOptionsList = [
    { label: 'Include Cancelled', controlName: FilterConfigEnum.IncludeCancelled },
    { label: 'Include Strikeouts', controlName: FilterConfigEnum.IncludeStrikeouts },
  ];

  public listObservationFindControlId = FindControlIdService.ListObservation;

  public listTimeSpanFindControlId = 'ListTimespan';

  public dateTypeFindControlId = FindControlIdService.ListMisc;

  public drugsFindControlId = FindControlIdService.FindDrugName;

  public listObservationAdditionalParameters: FindControlAdditionalSearchParametersDto = {
    Type: FindControlAdditionalSearchParametersType.Standard,
    AdditionalSearchParameters: [
      {
        ColumnName: 'ObservationTypeID',
        Value: '2',
        Negate: false
      }
    ]
  };

  public dateTypeRequiredSearchParametersDto: FindControlRequiredSearchParametersDto = {
    RequiredSearchParameters: [
      {
        ColumnName: 'ListType',
        Value: 'ResultTrackingDateType',
        Negate: false
      }
    ]
  };

  public defaultFormValues = {
    groupType: GroupTypeEnum.Date,
    maxItemsReturned: -1,
    filterVisibility: VisibleToEnum.Me
  };

  public isObservationFindControlDisabled = false;

  public isMedicationFindControlDisabled = false;

  ngOnInit(): void {
    this.listenGroupTypeChanges();
    this.listenMedicationChanges();
  }

  private listenGroupTypeChanges(): void {
    this.patientResultsFilterDetailsFormGroup.controls.groupType.valueChanges.pipe(
      debounceTime(200),
      startWith(this.patientResultsFilterDetailsFormGroup.controls.groupType.value),
      tap((groupType: LabResultSortType) => {
        const enableFindControlGroupTypes = [LabResultSortType.Template, LabResultSortType.Flowsheet];
        if (enableFindControlGroupTypes.includes(groupType)) {
          this.isObservationFindControlDisabled = false;
        } else {
          this.isObservationFindControlDisabled = true;
        }
      }),
      this.takeUntilDestroy()
    ).subscribe();
  }

  private listenMedicationChanges(): void {
    this.patientResultsFilterDetailsFormGroup.controls.showMedicationOnFlowSheet.valueChanges.pipe(
      debounceTime(200),
      startWith(this.patientResultsFilterDetailsFormGroup.controls.showMedicationOnFlowSheet.value),
      tap((showMedicationOnFlowSheet: boolean) => {
        this.isMedicationFindControlDisabled = !showMedicationOnFlowSheet;
      }),
      this.takeUntilDestroy()
    ).subscribe();
  }

  public handleObservationChipChange(events: IColumnPk[] | null): void {
    const selectedTemplateDetails: LabTemplateDetailsDto[] = [];
    for (const event of events || []) {
      const templateId = event.Pk ? Guid._coerceToGuidStr(event.Pk) : null;
      if (templateId) {
        const templateDetail: LabTemplateDetailsDto = {
          TemplateName: event.Name ?? '',
          TemplateUid: templateId
        };
        selectedTemplateDetails.push(templateDetail);
      }
    }
    this.patientResultsFilterDetailsFormGroup.controls.labTemplateDetails.setValue(selectedTemplateDetails);
  }

  public handleDrugsChipChange(events: IColumnPk[] | null): void {
    const selectedDrugDetails: GenericDrugDetailsDto[] = [];
    for (const event of events || []) {
      const drugId = event.Pk ? Guid._coerceToGuidStr(event.Pk) : null;
      if (drugId) {
        const drug: GenericDrugDetailsDto = {
          GenericDrugName: event.DescDisplay ?? '',
          GenericDrugNameUid: drugId,
          GenericDrugNameID: event.GenericDrugNameID
        };
        selectedDrugDetails.push(drug);
      }
    }
    this.patientResultsFilterDetailsFormGroup.controls.genericDrugDetails.setValue(selectedDrugDetails);
  }

  protected handleDateRangeSelectedPksChange(selectedPks: GuidStrValue[] | null): void {
    const normalizedSelectedPks = selectedPks ?? [];
    this.patientResultsFilterDetailsFormGroup.controls.dateRangeUid.setValue(normalizedSelectedPks);
  }

  protected onCustomDateRangeChange(dateRange: ApCustomDateRange): void {
    this.customDateRange = dateRange;
    this.customDateRangeChange.emit(dateRange);
  }

  get selectedTimeSpanIds(): GuidStrValue[] {
    return this.patientResultsFilterDetailsFormGroup.controls.dateRangeUid.value ?? [];
  }
}
